import { useOverlayClose } from "@/hooks/useOverlayClose";
import React, { ReactNode, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";

export type ModalSize =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "fit"
  | "full";

export type OverlayType = "one" | "two";

export interface ModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  headerBg?: string;
  overlayType?: OverlayType;
  closeOnOutsideClick?: boolean;
  maxHeight?: string;
  maxWidth?: string;
  width?: string | number;
  height?: string | number;
  title: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  withoutFooter?: boolean;
  showLogo?: boolean;
  logoSrc?: string;
  onClickX?: () => void;
}

const SIZE_CLASSES: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-full",
  fit: "max-w-fit",
};

function useBodyScrollLock(isOpen: boolean) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
}

function useEscClose(isOpen: boolean, onClose: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);
}

export const ModalBase: React.FC<ModalBaseProps> = ({
  isOpen,
  onClose,
  size = "md",
  headerBg = "#117baa",
  overlayType = "one",
  closeOnOutsideClick = true,
  maxHeight,
  maxWidth,
  width,
  height,
  title,
  icon,
  children,
  showLogo = false,
  logoSrc,
  onClickX,
}) => {
  useBodyScrollLock(isOpen);
  useEscClose(isOpen, onClose);
  const { handleOverlayMouseDown, handleOverlayClick } = useOverlayClose(onClose, closeOnOutsideClick);

  const handleXClick = useCallback(() => {
    onClickX?.();
    onClose();
  }, [onClickX, onClose]);

  const overlayClass =
    overlayType === "one"
      ? "bg-black/30 backdrop-blur"
      : "bg-none backdrop-invert-[80%] backdrop-blur-[2px]";

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex justify-center items-center"
      style={{ zIndex: 1001 }}
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
    >
      {/* Overlay */}
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${overlayClass}`}
        aria-hidden="true"
      />
      {/* Container do modal */}
      <div
        className={`relative bg-white rounded-3xl shadow-xl w-full mx-4 overflow-hidden flex flex-col
                   transform transition-all duration-200  animate-in fade-in zoom-in-95
                   ${SIZE_CLASSES[size] ?? "max-w-md"}`}
        style={{
          maxWidth: maxWidth,
          width,
          height,
          maxHeight,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-base-title"
      >
        {/* Header com título e botão de fechar */}
        <div
          className="relative flex items-center gap-2 px-4 py-2 rounded-t-3xl shrink-0"
          style={{ backgroundColor: headerBg }}
        >
          {/* Ícone antes do título */}
          {icon && (
            <span className="flex justify-center items-center max-h-[15px] text-white shrink-0">
              {icon}
            </span>
          )}

          {/* Título */}
          <h2
            id="modal-base-title"
            className="flex-1 m-0 font-medium text-white text-base"
          >
            {title}
          </h2>

          {/* Botão X */}
          <button
            onClick={handleXClick}
            className="flex justify-center items-center hover:bg-white/20 border border-white rounded-full focus:outline-none focus:ring-2 focus:ring-white/50 w-6 h-6 text-white text-xs transition-colors shrink-0"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default ModalBase;
